import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Download, ChevronLeft, ChevronRight, TrendingUp, Plus, Trash2, MessageCircle } from 'lucide-react';
import { User } from '../../models/User';
import { MarketplaceActivity as MarketplaceActivityType, AdminActivityFilter, AdminActivityResponse } from '../../models/AdminActivity';
import { getMarketplaceActivities } from '../../data/adminActivityDatabase.ts';
import { 
  formatTimestamp, 
  formatActivityAction, 
  generatePageNumbers, 
  exportToCSV, 
  searchActivities,
  getActivityDetails,
  formatDateForFilename,
  getActionIcon as getSharedActionIcon,
  getActionColor as getSharedActionColor
} from '../../utils/adminUtils';
import { MetricCard } from './MetricCard';

interface MarketplaceActivityProps {
  user: User;
}

function MarketplaceActivity({ user }: MarketplaceActivityProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100 | 250>(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [activitiesData, setActivitiesData] = useState<AdminActivityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load data with pagination
  useEffect(() => {
    const loadActivities = async () => {
      setIsLoading(true);
      try {
        const data = await getMarketplaceActivities(currentPage, pageSize);
        setActivitiesData(data);
      } catch (error) {
        console.error('Failed to load marketplace activities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [currentPage, pageSize]);

  const marketplaceActivities = activitiesData?.activities as MarketplaceActivityType[] || [];
  const pagination = activitiesData?.pagination || { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 25 };

  // Apply filters
  const filteredActivities = useMemo(() => {
    let filtered = marketplaceActivities;
    
    if (actionFilter !== 'all') {
      filtered = filtered.filter(activity => activity.action === actionFilter);
    }
    
    return searchActivities(filtered, searchTerm) as MarketplaceActivityType[];
  }, [marketplaceActivities, searchTerm, actionFilter]);

  const handleExport = () => {
    exportToCSV(filteredActivities, `marketplace-activities-${formatDateForFilename(new Date())}.csv`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: 25 | 50 | 100 | 250) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getActionIcon = (action: string) => getSharedActionIcon(action, 'marketplace');
  const getActionColor = (action: string) => getSharedActionColor(action, 'marketplace');

  // Calculate statistics
  const createActions = marketplaceActivities.filter(a => a.action.includes('create')).length;
  const deleteActions = marketplaceActivities.filter(a => a.action.includes('delete')).length;
  const contactActions = marketplaceActivities.filter(a => a.action.includes('contact')).length;

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Created Offers/Requests"
          value={createActions}
          icon={Plus}
          color="green"
        />
        <MetricCard
          title="Deleted Items"
          value={deleteActions}
          icon={Trash2}
          color="red"
        />
        <MetricCard
          title="Contact Actions"
          value={contactActions}
          icon={MessageCircle}
          color="blue"
        />
        <MetricCard
          title="Total Activities"
          value={pagination.totalItems}
          icon={TrendingUp}
          color="indigo"
        />
      </div>

      {/* Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search and Filters */}
            <div className="flex-1 flex flex-col sm:flex-row gap-4">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search by user, entity ID, or contacted person..."
                  />
                </div>
              </div>
              
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="all">All Actions</option>
                <option value="create_sale_offer">Created Sale Offers</option>
                <option value="create_buy_request">Created Buy Requests</option>
                <option value="delete_sale_offer">Deleted Sale Offers</option>
                <option value="delete_buy_request">Deleted Buy Requests</option>
                <option value="contact_buyer">Contacted Buyers</option>
                <option value="contact_seller">Contacted Sellers</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExport}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Activities Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Marketplace Activities ({pagination.totalItems})
            </h3>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value) as 25 | 50 | 100 | 250)}
              className="block w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={250}>250 per page</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActivities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                        {getActionIcon(activity.action)}
                        <span className="ml-1">{formatActivityAction(activity)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {activity.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatTimestamp(activity.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                        activity.entityType === 'sale_offer' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {activity.entityType === 'sale_offer' ? 'Sale Offer' : 'Buy Request'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {activity.entityId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getActivityDetails(activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage >= pagination.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, pagination.totalItems)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalItems}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {generatePageNumbers(currentPage, pagination.totalPages).map((pageNum, index) => (
                      pageNum === -1 ? (
                        <span key={`dots-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.totalPages, currentPage + 1))}
                      disabled={currentPage >= pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarketplaceActivity; 